import React, {
	createContext,
	useContext,
	useReducer,
	type ComponentPropsWithoutRef,
	type Dispatch,
	type SetStateAction,
} from 'react';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createActionsHook, createStateHook, createStore } from '@atlassian/react-sweet-state';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';

const noop = () => {
	/* empty */
};
const CustomThemeDataContext = createContext<CustomThemeData>({});
const SetCustomThemeDataContext =
	createContext<Dispatch<Partial<SetStateAction<CustomThemeData>>>>(noop);

export const useCustomThemeData = functionWithCondition(
	() => fg('jfp-magma-fix-custom-theme-data-hydration-error'),
	() => useCustomThemeContextState(),
	() => useContext(CustomThemeDataContext),
);
export const useSetCustomThemeData = functionWithCondition(
	() => fg('jfp-magma-fix-custom-theme-data-hydration-error'),
	() => useCustomThemeContextActions().update,
	() => useContext(SetCustomThemeDataContext),
);

/**
 *
 * Collects custom theme data and provides it via context to be used by CustomThemeAnalytics.
 *
 */
function CustomThemeDataProviderOld({ children }: React.PropsWithChildren): React.JSX.Element {
	const [state, dispatch] = useReducer(reducer, {});

	return (
		<CustomThemeDataContext.Provider value={state}>
			<SetCustomThemeDataContext.Provider value={dispatch}>
				{children}
			</SetCustomThemeDataContext.Provider>
		</CustomThemeDataContext.Provider>
	);
}

export const CustomThemeDataProvider = componentWithFG<
	React.PropsWithChildren,
	ComponentPropsWithoutRef<typeof CustomThemeDataProviderOld>
>(
	'jfp-magma-fix-custom-theme-data-hydration-error',
	({ children }) => <>{children}</>,
	CustomThemeDataProviderOld,
);

const reducer = (state: CustomThemeData, action: Partial<CustomThemeData>): CustomThemeData => {
	return {
		...state,
		...action,
	};
};

type CustomThemeData = {
	cloudId?: string;
	customLogoSize?: {
		width?: number;
		height?: number;
	};
	customSiteTitleLength?: number;
	hasCustomBGColor?: boolean;
	hasCustomHighlightColor?: boolean;
	hasCustomLogo?: boolean;
	hasCustomSiteTitle?: boolean;
};

const initialState: CustomThemeData = {};

const store = createStore({
	name: 'navigation-custom-theme-context',
	initialState,
	actions: {
		update:
			(customThemeData: Partial<CustomThemeData>) =>
			({ setState }) => {
				setState(customThemeData);
			},
	},
});

const useCustomThemeContextState = createStateHook(store);
const useCustomThemeContextActions = createActionsHook(store);
